import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Wrench, Award, Tag, Home as HomeIcon } from "lucide-react";
import { businessInfo, applianceMeta, trustBadges, whyChoose, howItWorks } from "../constants";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import PinChecker from "../components/PinChecker";
import ServicesOverview from "../components/ServicesOverview";
import PricingPreview from "../components/PricingPreview";
import TestimonialsBlock from "../components/TestimonialsBlock";
import FaqBlock from "../components/FaqBlock";

const trustIcons = { "Trained Technicians": Wrench, "Genuine Spare Parts": Award, "Transparent Pricing": Tag, "Doorstep Service": HomeIcon };

export default function Home() {
  return (
    <Layout>
      <SEO
        title="Elica Service Centre in Pune | Revive Solutions – Chimney, Hob, Oven, Dishwasher"
        description="Authorised Elica service centre in Pune. Book doorstep repair, cleaning, installation and maintenance for Elica chimneys, hobs, cooktops, ovens, microwaves and dishwashers. Transparent pricing. Genuine spares."
        canonical="/"
      />

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#0b1220] to-[#1a2438] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 kenburns">
          <img src="https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Modern Elica kitchen" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220] via-[#0b1220]/85 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <div className="chip chip-dark mb-6">Authorised Elica Service Provider</div>
            <h1 className="font-display text-4xl md:text-6xl font-medium leading-[1.05] mb-6">
              Expert Elica Kitchen Appliance Service in Pune
            </h1>
            <p className="text-lg text-white/75 mb-8 max-w-xl">
              Professional repair, installation, cleaning and maintenance for Elica chimneys, hobs, cooktops, ovens, microwave ovens and dishwashers.
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-10">
              <Link to="/book-service" className="btn-primary">Book a Service <ArrowRight className="w-4 h-4" /></Link>
              <a href={businessInfo.whatsapp} target="_blank" rel="noreferrer" className="btn-outline-light"><MessageCircle className="w-4 h-4" /> WhatsApp Us</a>
            </div>
            {/* Trust strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {trustBadges.map((t) => {
                const Icon = trustIcons[t];
                return (
                  <div key={t} className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-white/10 grid place-items-center"><Icon className="w-4 h-4 text-[#e30613]" /></div>
                    <div className="text-sm font-medium">{t}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* PIN checker card */}
          <div className="reveal">
            <PinChecker autoRedirect />
          </div>
        </div>
      </section>

      {/* SELECT APPLIANCE */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="chip mb-4">Choose Appliance</div>
            <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight">What appliance do you need help with?</h2>
            <p className="text-slate-500 mt-4">Pick your Elica appliance to see the specific services we offer.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {Object.values(applianceMeta).map((a) => (
              <Link key={a.slug} to={a.route} className="hover-card group relative overflow-hidden border border-neutral-200 rounded-lg bg-white">
                <div className="img-zoom aspect-[4/3] bg-neutral-100">
                  <img src={a.image} alt={`Elica ${a.title}`} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#e30613] font-semibold mb-1">Elica</div>
                  <h3 className="font-display text-xl font-medium mb-1.5 group-hover:text-[#e30613] transition-colors">{a.title}</h3>
                  <p className="text-sm text-slate-500 mb-3 min-h-[40px]">{a.tagline}</p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#0f172a] group-hover:text-[#e30613] transition-colors">View Services <ArrowRight className="w-3.5 h-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <ServicesOverview />

      {/* HOW IT WORKS */}
      <section className="py-20 section-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="chip mb-4">How it works</div>
            <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight">Book a service in under a minute</h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {howItWorks.map((s) => (
              <div key={s.step} className="bg-white p-6 rounded-lg border border-neutral-200 hover-card">
                <div className="step-dot bg-[#e30613] text-white mb-4">{s.step}</div>
                <h3 className="font-semibold text-[15px] mb-2">{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/book-service" className="btn-primary">Start Booking <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="chip mb-4">Why Revive Solutions</div>
              <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight mb-6">A dedicated Elica service you can rely on</h2>
              <p className="text-slate-500 leading-relaxed max-w-lg mb-6">
                As an authorised Elica service provider, we focus only on Elica kitchen appliances. No dabbling in ACs, fridges or washing machines – just kitchen expertise, done properly.
              </p>
              <Link to="/about" className="btn-ghost">About Us <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {whyChoose.map((w) => (
                <div key={w.title} className="p-6 rounded-lg border border-neutral-200 hover-card">
                  <div className="w-9 h-9 rounded-md bg-[#fef2f2] grid place-items-center mb-3"><Wrench className="w-4 h-4 text-[#e30613]" /></div>
                  <h3 className="font-semibold text-[15px] mb-2">{w.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <PricingPreview />

      {/* TESTIMONIALS */}
      <TestimonialsBlock />

      {/* FAQ */}
      <FaqBlock />

      {/* FINAL CTA */}
      <section className="section-dark py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="chip chip-dark mb-5">Ready to book?</div>
          <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight mb-5">Trusted Elica service, one call away</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">Book online in a minute or drop us a WhatsApp – our team will confirm your slot.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/book-service" className="btn-primary">Book a Service <ArrowRight className="w-4 h-4" /></Link>
            <a href={businessInfo.whatsapp} target="_blank" rel="noreferrer" className="btn-outline-light"><MessageCircle className="w-4 h-4" /> Chat on WhatsApp</a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
